import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import SpeczeLogo from '../../assets/SpeczeLogo'
import { Alert } from '../../components/ui'
import api from '../../lib/axios'
import toast from 'react-hot-toast'

/**
 * Landing page for the link in a password-reset email.
 *
 * The API mails a link to {FRONTEND_URL}/reset-password?token=…&email=…
 * (see AppServiceProvider::configurePasswordResetUrl). Both values are read
 * straight from the query string and handed back to POST /auth/reset-password,
 * which is what Laravel's password broker needs to validate the token.
 */
export default function ResetPassword() {
  const [params]  = useSearchParams()
  const navigate  = useNavigate()

  const token = params.get('token') || ''
  const email = params.get('email') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm]   = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')

  const handleSubmit = async e => {
    e.preventDefault()
    setError('')

    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/reset-password', {
        token,
        email,
        password,
        password_confirmation: confirm,
      })
      toast.success('Password updated — please sign in.')
      navigate('/login')
    } catch (err) {
      setError(
        err.response?.data?.errors?.email?.[0] ||
        err.response?.data?.errors?.password?.[0] ||
        err.response?.data?.message ||
        'Unable to reset your password.'
      )
    } finally {
      setLoading(false)
    }
  }

  const shell = children => (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #07020f 0%, #180a3a 50%, #07020f 100%)' }}>
      <div className="absolute inset-0 stars-bg opacity-60" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full blur-3xl bg-purple-600/15 pointer-events-none" />

      <div className="relative z-10 w-full max-w-[400px] mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5"><SpeczeLogo size={44} /></div>
          <h1 className="font-display font-bold text-2xl text-white">Choose a new password</h1>
          {email
            ? <p className="text-white/40 text-sm mt-1">for <strong className="text-white/70">{email}</strong></p>
            : <p className="text-white/40 text-sm mt-1">Set a new password for your account</p>}
        </div>

        <div className="glass-card p-7">{children}</div>

        <p className="text-center text-sm text-white/40 mt-6">
          <Link to="/login" className="text-purple-400 hover:text-purple-300 font-semibold transition-colors">
            ← Back to sign in
          </Link>
        </p>
      </div>
    </div>
  )

  // A link that lost its query string cannot be completed — send them back to
  // request a fresh one rather than showing a form that is guaranteed to fail.
  if (!token || !email) {
    return shell(
      <div className="text-center py-4">
        <div className="text-4xl mb-4">🔗</div>
        <p className="font-semibold text-white mb-2">This reset link is incomplete</p>
        <p className="text-sm text-white/50 mb-6">
          It may have been truncated by your email client, or already used.
        </p>
        <Link to="/forgot-password" className="btn-specze inline-flex px-5 py-2.5 text-white text-sm"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
          Request a new link
        </Link>
      </div>
    )
  }

  return shell(
    <>
      {error && <div className="mb-5"><Alert type="error">{error}</Alert></div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label-specze">New password</label>
          <input className="input-specze" type="password" required autoFocus minLength={8}
            value={password} onChange={e => setPassword(e.target.value)}
            placeholder="At least 8 characters" />
        </div>
        <div>
          <label className="label-specze">Confirm new password</label>
          <input className="input-specze" type="password" required minLength={8}
            value={confirm} onChange={e => setConfirm(e.target.value)}
            placeholder="Re-enter your new password" />
        </div>
        <button type="submit" disabled={loading} className="btn-specze w-full py-3 text-white"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)', boxShadow: '0 0 20px rgba(139,63,255,0.3)' }}>
          {loading ? 'Updating…' : 'Update Password'}
        </button>
      </form>
    </>
  )
}
